/**
 * O estado do editor em lote e a tradução dele pro patch do servidor.
 *
 * Separado da tela pra ser testável sem render: a regra que importa é
 * "só viaja o que a pessoa MEXEU". Polaridade e abordagem têm três
 * estados (não mexer / sem classificação / um valor), e `null` é valor
 * — por isso o campo carrega `touched` em vez de usar `null` como
 * ausência. As listas têm um MODO, porque num lote "a lista final" não
 * existe: cada menção tem a sua, e o servidor traduz `add`/`remove`/
 * `replace` por menção (`core/bulk_edit/patch.py`, dono do formato).
 */

import type { BulkListMode, BulkPatchWire, CommonTheme } from "@/shared/api/api-client";

export type ScalarField = { touched: boolean; value: string | null };
export type ListField = { mode: "keep" | BulkListMode; ids: number[] };

export type BulkDialogState = {
  polarity: ScalarField;
  approach: ScalarField;
  themes: ListField;
  keyMessages: ListField;
  spokespersons: ListField;
  /** Revisão: não mexer, marcar ou desmarcar. */
  reviewed: "keep" | "yes" | "no";
  /** Tema em destaque: `null` não mexe. */
  highlight: CommonTheme | null;
};

export const emptyBulkDialogState = (): BulkDialogState => ({
  polarity: { touched: false, value: null },
  approach: { touched: false, value: null },
  themes: { mode: "keep", ids: [] },
  keyMessages: { mode: "keep", ids: [] },
  spokespersons: { mode: "keep", ids: [] },
  reviewed: "keep",
  highlight: null,
});

/**
 * As opções de "tema em destaque" do lote: só tema que TODAS as menções
 * têm (decisão da dona, 2026-10-03).
 *
 * `common` é o que o servidor contou no alvo como está AGORA. Se o
 * mesmo lote também mexe nos temas, o conjunto muda, e a conta
 * acompanha:
 *
 * - **adicionar** a todas: os adicionados passam a estar em todas;
 * - **substituir** em todas: só os substitutos estão em todas;
 * - **remover** de todas: o tema removido sai — e sai inteiro, mesmo
 *   que só UM dos ids do nome tenha sido removido (a marca dona daquele
 *   id fica sem ele, então o nome deixa de estar em todas).
 *
 * `catalog` dá o nome dos ids do campo de temas. Id fora do catálogo é
 * ignorado: sem nome não há como oferecê-lo.
 */
export function highlightOptions(
  common: CommonTheme[],
  themes: ListField,
  catalog: Array<{ id: number; name: string }>
): CommonTheme[] {
  if (themes.mode === "keep") return common;
  const nameOf = new Map(catalog.map(item => [item.id, item.name]));
  const byName = new Map<string, Set<number>>();
  const put = (name: string, ids: number[]) => {
    const current = byName.get(name) ?? new Set<number>();
    for (const id of ids) current.add(id);
    byName.set(name, current);
  };
  if (themes.mode === "remove") {
    const removed = new Set(themes.ids);
    for (const theme of common) {
      if (!theme.theme_ids.some(id => removed.has(id))) put(theme.name, theme.theme_ids);
    }
  } else {
    if (themes.mode === "add") {
      for (const theme of common) put(theme.name, theme.theme_ids);
    }
    for (const id of themes.ids) {
      const name = nameOf.get(id);
      if (name !== undefined) put(name, [id]);
    }
  }
  return [...byName.entries()]
    .map(([name, ids]) => ({ name, theme_ids: [...ids].sort((a, b) => a - b) }))
    .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
}

/** O valor do `<select>` que significa "não mexer" — fora do domínio
 *  de polaridade e do vocabulário de abordagem, então não colide. */
export const KEEP = "__keep__";

/**
 * Monta o patch. Dimensão não tocada fica AUSENTE (o servidor lê
 * ausência como "não mexa"). `add`/`remove` sem id não viajam: o
 * servidor os recusaria como "não muda nada", e aqui é mais honesto
 * tratar como não tocado. `replace` sem id viaja — esvaziar é uma
 * correção legítima.
 */
export function buildBulkPatch(state: BulkDialogState): BulkPatchWire {
  const patch: BulkPatchWire = {};
  if (state.polarity.touched) patch.polarity = { value: state.polarity.value };
  if (state.approach.touched) patch.approach = { value: state.approach.value };
  const lists: Array<[keyof BulkPatchWire, ListField]> = [
    ["themes", state.themes],
    ["key_messages", state.keyMessages],
    ["spokespersons", state.spokespersons],
  ];
  for (const [key, field] of lists) {
    if (field.mode === "keep") continue;
    if (field.mode !== "replace" && field.ids.length === 0) continue;
    patch[key] = { mode: field.mode, ids: [...new Set(field.ids)] } as never;
  }
  if (state.reviewed !== "keep") {
    patch.reviewed = { value: state.reviewed === "yes" };
  }
  if (state.highlight && state.highlight.theme_ids.length > 0) {
    patch.highlight = { theme_ids: [...new Set(state.highlight.theme_ids)] };
  }
  return patch;
}

export function isBulkPatchEmpty(patch: BulkPatchWire): boolean {
  return Object.keys(patch).length === 0;
}

/** Quantas dimensões o patch toca — o botão diz "Aplicar 2 correções". */
export function bulkPatchSize(patch: BulkPatchWire): number {
  return Object.keys(patch).length;
}

/** O lote é só de descarte ou só de restauração? Devolve o valor, ou `null`
 *  quando o patch mexe em qualquer outra coisa. */
function discardOnlyValue(patch: BulkPatchWire): boolean | null {
  const keys = Object.keys(patch).filter(key => key !== "schema_version");
  if (keys.length !== 1 || keys[0] !== "discarded") return null;
  return patch.discarded?.value ?? null;
}

/** A palavra do desfecho do lote, no singular ou no plural: "corrigida",
 *  "descartadas", "restaurada"… O banner não pode dizer "corrigidas" de
 *  um lote de descarte. */
export function bulkOutcomeWord(patch: BulkPatchWire, count: number): string {
  const plural = count !== 1;
  const value = discardOnlyValue(patch);
  if (value === true) return plural ? "descartadas" : "descartada";
  if (value === false) return plural ? "restauradas" : "restaurada";
  return plural ? "corrigidas" : "corrigida";
}

/** O verbo do andamento: "Corrigindo", "Descartando" ou "Restaurando". */
export function bulkProgressVerb(patch: BulkPatchWire): "Corrigindo" | "Descartando" | "Restaurando" {
  const value = discardOnlyValue(patch);
  if (value === true) return "Descartando";
  if (value === false) return "Restaurando";
  return "Corrigindo";
}
